import { FileClock } from 'lucide-react';

import { isAwaitingClosure, type ProjectClockView } from '../lib/flags';
import { StatusBadge, StatusPill } from '@/shared/components/StatusBadge';
import { messages } from '@/shared/constants/messages';

interface ProjectStatusBadgeProps {
  project: ProjectClockView;
  className?: string;
  /** Injetável só para teste — a tela sempre lê o relógio de verdade. */
  now?: Date;
}

/**
 * O ÚNICO crachá de projeto do painel (decisão 29).
 *
 * Ele responde "em que pé está isto?" juntando as duas coisas que o usuário não
 * distingue e nem deveria: a situação da esteira e o que o RELÓGIO já disse
 * sobre ela. Um projeto de junho que ainda aparece como "Aprovado" em agosto é
 * o crachá mentindo — `isAwaitingClosure` sabe a verdade desde a 1ª rodada, e
 * até aqui só o cabeçalho do detalhe a mostrava, num segundo crachá ao lado.
 *
 * **"Aguardando relatório" não é status, e não pode virar um** (decisão 29,
 * precedente direto da decisão 3 da spec do formulário): valor de enum que
 * ninguém escreve exigiria job agendado e ator fantasma no log. É expressão —
 * e por ser expressão, o dia em que `submit_report` existir (v1.1) todos os
 * projetos fecham sozinhos, sem migration de correção de dado.
 */
export function ProjectStatusBadge({ project, className, now }: ProjectStatusBadgeProps) {
  if (isAwaitingReport(project, now)) {
    return (
      <StatusPill
        label={messages.review.awaitingReport}
        tone="warning"
        icon={FileClock}
        className={className}
      />
    );
  }

  return <StatusBadge status={project.status} className={className} />;
}

/**
 * `switch` exaustivo e **sem `default`**, como `timelineFor` e `attributionFor`:
 * situação nova no enum quebra o build em vez de sair sem crachá ou, pior, com
 * o crachá errado por omissão.
 */
function isAwaitingReport(project: ProjectClockView, now?: Date): boolean {
  switch (project.status) {
    case 'approved':
      return now ? isAwaitingClosure(project, now) : isAwaitingClosure(project);

    case 'draft':
    case 'under_review':
    case 'changes_requested':
    case 'rejected':
    case 'cancelled':
    case 'completed':
      return false;
  }
}
